<template>
    <v-card elevation="2" class="ma-1">
        <v-card-text>
            <div class="d-flex flex-wrap align-center mb-3" style="gap: 8px;">
                <v-btn-toggle v-model="status" mandatory dense color="primary">
                    <v-btn small value="aberta">Abertas</v-btn>
                    <v-btn small value="paga">Pagas</v-btn>
                    <v-btn small value="cancelada">Canceladas</v-btn>
                    <v-btn small value="todas">Todas</v-btn>
                </v-btn-toggle>
                <v-spacer/>
                <span class="subtitle-2">
                    {{ total === 1 ? '1 solicitação' : `${total} solicitações` }}
                    <template v-if="status === 'aberta' && total"> · R$ {{ formatPrice(somaPagina) }} na página</template>
                </span>
                <v-btn icon :loading="carregando" @click="consultar">
                    <v-icon>mdi-refresh</v-icon>
                </v-btn>
            </div>

            <v-data-table
                :headers="headers"
                :items="solicitacoes"
                :loading="carregando"
                :server-items-length="total"
                :options.sync="options"
                :footer-props="{ 'items-per-page-options': [10, 25, 50] }"
                :mobileBreakpoint="0"
                :expanded.sync="expandidos"
                show-expand
                single-expand
                item-key="id"
                disable-sort
                no-data-text="Nenhuma solicitação"
            >
                <template v-slot:item.created_at="{ item }">
                    {{ formatDate(item.created_at, 'DD/MM/YYYY HH:mm') }}
                </template>

                <template v-slot:item.empresa="{ item }">
                    <div style="min-width: 160px;">
                        <a @click="$router.push(`/financeiro/${item.empresa_id}`)">#{{ item.empresa_id }}</a>
                        {{ item.empresa ? item.empresa.fantasia : '' }}
                        <div v-if="item.empresa && whatsappLink(item.empresa.whatsapp)" class="caption">
                            <a :href="whatsappLink(item.empresa.whatsapp)" target="_blank">
                                <v-icon x-small color="success">mdi-whatsapp</v-icon>
                                {{ item.empresa.whatsapp }}
                            </a>
                        </div>
                    </div>
                </template>

                <template v-slot:item.valor="{ item }">
                    <div style="white-space: nowrap;">
                        <b>R$ {{ formatPrice(item.valor) }}</b>
                        <div class="caption">{{ item.quantidade === 1 ? '1 indicação' : `${item.quantidade} indicações` }}</div>
                    </div>
                </template>

                <template v-slot:item.pix="{ item }">
                    <div style="min-width: 180px;">
                        <span class="caption grey--text">{{ item.tipo_pix || 'Chave' }}:</span>
                        {{ item.chave_pix }}
                        <v-btn icon x-small @click="copiar(item.chave_pix)">
                            <v-icon x-small>mdi-content-copy</v-icon>
                        </v-btn>
                        <div v-if="item.nome_pix" class="caption">{{ item.nome_pix }}</div>
                    </div>
                </template>

                <template v-slot:item.status="{ item }">
                    <div style="min-width: 140px;">
                        <v-chip x-small dark :color="situacoes[item.status].cor">{{ situacoes[item.status].texto }}</v-chip>
                        <div v-if="item.status === 'paga'" class="caption grey--text">
                            {{ formatDate(item.pago_em, 'DD/MM/YYYY HH:mm') }}
                            <template v-if="item.pagador"> · {{ item.pagador.name }}</template>
                        </div>
                        <div v-if="item.status === 'cancelada'" class="caption grey--text">{{ item.cancelamento_motivo }}</div>
                        <div v-if="item.observacao" class="caption grey--text">{{ item.observacao }}</div>
                    </div>
                </template>

                <template v-slot:item.acoes="{ item }">
                    <div v-if="item.status === 'aberta'" class="d-flex" style="gap: 4px;">
                        <v-btn
                            v-if="podeMarcarPago"
                            x-small
                            depressed
                            color="success"
                            @click="abrir('pagar', item)"
                        >
                            Marcar como pago
                        </v-btn>
                        <v-btn
                            v-if="podeEditar"
                            x-small
                            text
                            color="red"
                            @click="abrir('cancelar', item)"
                        >
                            Cancelar
                        </v-btn>
                    </div>
                </template>

                <template v-slot:expanded-item="{ headers: colunas, item }">
                    <td :colspan="colunas.length" class="py-2">
                        <div class="caption grey--text mb-1">
                            Pedida por {{ item.solicitante ? item.solicitante.name : '—' }}
                        </div>
                        <div v-for="ind in item.indicacoes" :key="ind.id" class="caption">
                            #{{ ind.id }} · {{ ind.nome_indicado }}
                            <template v-if="ind.empresa_indicada_id"> · empresa #{{ ind.empresa_indicada_id }}</template>
                            · pagou em {{ formatDate(ind.cliente_em, 'DD/MM/YYYY') }}
                            · R$ {{ formatPrice(ind.valor_comissao) }}
                        </div>
                    </td>
                </template>
            </v-data-table>
        </v-card-text>

        <MotivoDialog
            :visible="dialog === 'pagar'"
            :carregando="executando"
            :texto="selecionada ? `R$ ${formatPrice(selecionada.valor)} para ${selecionada.chave_pix}${selecionada.nome_pix ? ` (${selecionada.nome_pix})` : ''}. Confirme depois de fazer o Pix.` : ''"
            :obrigatorio="false"
            titulo="Marcar como pago"
            label="Observação (ex.: id do comprovante)"
            botao="Marcar como pago"
            cor="success"
            @close="dialog = null"
            @confirmar="pagar"
        />

        <MotivoDialog
            :visible="dialog === 'cancelar'"
            :carregando="executando"
            texto="As comissões voltam para disponível e a loja pode pedir de novo."
            titulo="Cancelar solicitação"
            botao="Cancelar solicitação"
            cor="red darken-1"
            @close="dialog = null"
            @confirmar="cancelar"
        />
    </v-card>
</template>

<script>
import MotivoDialog from './MotivoDialog.vue';
import { SOLICITACOES, whatsappLink } from './status';

export default {
    name: 'TabSolicitacoes',

    components: { MotivoDialog },

    props: {
        podeEditar: { type: Boolean, default: false },
        podeMarcarPago: { type: Boolean, default: false },
    },

    data: () => ({
        status: 'aberta',
        carregando: false,
        executando: false,
        solicitacoes: [],
        total: 0,
        options: { page: 1, itemsPerPage: 25 },
        expandidos: [],
        dialog: null,
        selecionada: null,
        situacoes: SOLICITACOES,
        headers: [
            { text: 'Nº', value: 'id', width: '60px' },
            { text: 'Pedida em', value: 'created_at' },
            { text: 'Loja', value: 'empresa' },
            { text: 'Valor', value: 'valor' },
            { text: 'Pix', value: 'pix' },
            { text: 'Situação', value: 'status' },
            { text: '', value: 'acoes', width: '190px' },
            { text: '', value: 'data-table-expand' },
        ],
    }),

    computed: {
        somaPagina() {
            return this.solicitacoes.reduce((acc, s) => acc + Number(s.valor), 0);
        },
    },

    watch: {
        status() {
            if (this.options.page !== 1) {
                this.options = { ...this.options, page: 1 };
                return;
            }

            this.consultar();
        },

        options: {
            handler() {
                this.consultar();
            },
            deep: true,
        },
    },

    methods: {
        whatsappLink,

        async consultar() {
            this.carregando = true;

            try {
                const { data } = await this.$http.get('gestao/indicacoes/solicitacoes', {
                    params: {
                        status: this.status,
                        page: this.options.page,
                        rows: this.options.itemsPerPage,
                    },
                });

                this.solicitacoes = data.data;
                this.total = data.total;
            } catch (e) {
                this.notify('Não foi possível carregar as solicitações', 'warning');
            } finally {
                this.carregando = false;
            }
        },

        abrir(acao, item) {
            this.selecionada = item;
            this.dialog = acao;
        },

        pagar(observacao) {
            this.executar(`gestao/indicacoes/solicitacoes/${this.selecionada.id}/pagar`, { observacao }, 'Solicitação marcada como paga');
        },

        cancelar(motivo) {
            this.executar(`gestao/indicacoes/solicitacoes/${this.selecionada.id}/cancelar`, { motivo }, 'Solicitação cancelada');
        },

        async executar(url, corpo, sucesso) {
            this.executando = true;

            try {
                const { data } = await this.$http.post(url, corpo);

                if (data.type !== 'success') {
                    this.notify(data.msg, 'warning');
                    return;
                }

                this.notify(sucesso);
                this.dialog = null;
                this.consultar();
                this.$emit('alterado');
            } catch (e) {
                this.notify('Não foi possível concluir a ação', 'warning');
            } finally {
                this.executando = false;
            }
        },

        copiar(valor) {
            navigator.clipboard.writeText(String(valor));
            this.notify('Copiado');
        },
    },
};
</script>
